import { Box, Button, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import {
  DATE_FORMAT_WEEK_DAY_MID_FNS,
  Facility,
  FilterKeys,
  GetGroupsQueryFilters,
  GroupType,
  PeriodTypeEnum,
} from '@schooly/api';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { useFacilities } from '@schooly/hooks/use-facilities';
import {
  AttentionTooltipIcon,
  CheckIcon,
  CircleIconButton,
  Counter,
  CrossIcon,
  ListRow,
  MinusIcon,
  ModalContent,
  ModalFooter,
  ModalSmall,
  PlusIcon,
  TagSubject,
  theme,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { FC, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { ModalEmptyMainArea } from '../../../../components/uikit-components/Modal/ModalEmptyMainArea';
import { ModalHeader } from '../../../../components/uikit-components/Modal/ModalHeader';
import { ModalPeopleExtensionPanel } from '../../../../components/uikit-components/Modal/ModalPeopleExtensionPanel';
import { useTimetable } from '../../../../context/timetable/WithTimetable';
import { hasAgeGroupOutsideScope } from '../../../Groups/utils';
import { getWeekdayLabel } from '../../SchoolTimetableStructures/utils';
import { TimetableAllocationDetailed, TimetableGroup, TimetableRoom } from '../types';
import { getGroupsByRoom, getRoomGroupNames, GroupForTimetable } from '../utils';
import { RoomCellSelect } from './RoomCellSelect';
import { TimetableAddGroupSidebar } from './TimetableAddGroupSidebar';
import { useGroupConflict } from './TimetableGroupConflictsContext';
import { SelectedSlotInfo } from './TimetableModalContent';

type AddGroupModalProps = {
  slot: SelectedSlotInfo;
  initialAllocations: TimetableAllocationDetailed[];
  onSave: (allocations: TimetableAllocationDetailed[]) => void;
  onClose: () => void;
  schoolId: string;
  dateRange: [string, string];
};

export const AddGroupModal: FC<AddGroupModalProps> = ({
  slot,
  initialAllocations,
  onSave,
  onClose,
  schoolId,
  dateRange,
}) => {
  const { formatMessage } = useIntl();
  const { getConfirmation } = useConfirmationDialog();
  const { structure } = useTimetable();

  const structureAgeGroupIds = structure?.age_group_ids ?? [];

  const { facilities, isLoading: isLoadingFacilities } = useFacilities(
    { schoolId },
    { enabled: !!schoolId },
  );

  const [selectedAllocations, setSelectedAllocations] =
    useState<TimetableAllocationDetailed[]>(initialAllocations);

  const initialRoomByGroupId = useMemo(
    () => new Map(initialAllocations.map((a) => [a.group.id, a.room?.id ?? null])),
    [initialAllocations],
  );

  const isDirty = useMemo(() => {
    if (selectedAllocations.length !== initialRoomByGroupId.size) return true;

    return selectedAllocations.some(({ group, room }) => {
      if (!initialRoomByGroupId.has(group.id)) return true;
      return initialRoomByGroupId.get(group.id) !== (room?.id ?? null);
    });
  }, [initialRoomByGroupId, selectedAllocations]);

  const handleClose = useCallback(async () => {
    if (!isDirty) {
      onClose();
      return;
    }

    const isConfirmed = await getConfirmation({
      textId: 'school-timetableStructures-CloseConfirmation',
    });

    if (isConfirmed) {
      onClose();
    }
  }, [isDirty, getConfirmation, onClose]);

  const defaultFilters = useMemo<GetGroupsQueryFilters>(
    () => ({
      date: dateRange,
      only_tutor_groups: slot.period.type === PeriodTypeEnum.Registration ? ['1'] : [],
      group_types: slot.period.type === PeriodTypeEnum.Lesson ? [GroupType.Subject] : [],
      [FilterKeys.AgeGroup]: structureAgeGroupIds,
    }),
    [dateRange, slot.period.type, structureAgeGroupIds],
  );

  const weekdayLabel = getWeekdayLabel(slot.weekday, DATE_FORMAT_WEEK_DAY_MID_FNS).toUpperCase();
  const periodName = slot.period.name;
  const timeRange = `${slot.timeSlot.start_time} - ${slot.timeSlot.end_time}`;

  const handleAddGroups = useCallback(
    (groups: GroupForTimetable[]) => {
      const newAllocations = groups.map((g) => {
        const defaultRoom = g.default_room_id
          ? facilities?.find((f) => f.id === g.default_room_id)
          : undefined;

        return {
          group: g,
          room: defaultRoom ? { id: defaultRoom.id, name: defaultRoom.name } : null,
        };
      });

      setSelectedAllocations((prev) => [...prev, ...newAllocations]);
    },
    [facilities],
  );

  const handleRemoveGroup = useCallback((group: TimetableGroup) => {
    setSelectedAllocations((prev) => prev.filter(({ group: g }) => g.id !== group.id));
  }, []);

  const handleSelectRoom = useCallback((groupId: string, room: TimetableRoom | null) => {
    setSelectedAllocations((prev) =>
      prev.map((a) => (a.group.id === groupId ? { ...a, room } : a)),
    );
  }, []);

  const groupsByRoom = useMemo(() => getGroupsByRoom(selectedAllocations), [selectedAllocations]);

  const handleSave = useCallback(() => {
    onSave(selectedAllocations);
  }, [onSave, selectedAllocations]);

  return (
    <ModalSmall open onClose={handleClose}>
      <ModalHeader
        active
        sx={{ py: 2 }}
        title={
          <Stack direction="row" alignItems="center" gap={2.5}>
            <Typography variant="h1">{weekdayLabel}</Typography>
            <Stack>
              <Typography variant="h3">
                {formatMessage({ id: 'schoolProperty-Period' })} {periodName}
              </Typography>
              <Typography color="common.grey2">{timeRange}</Typography>
            </Stack>
          </Stack>
        }
      >
        <IconButton onClick={handleClose} sx={{ mt: 1 }}>
          <CrossIcon />
        </IconButton>
      </ModalHeader>

      <ModalPeopleExtensionPanel
        titleId="section-Groups"
        addActionId="section-Groups"
        editActionId="section-Groups"
        count={selectedAllocations.length}
        active
        sidebarContent={
          <TimetableAddGroupSidebar
            defaultFilters={defaultFilters}
            selectedGroups={selectedAllocations.map((a) => a.group)}
            onSelectGroups={handleAddGroups}
          />
        }
      >
        <ModalContent active sx={{ pt: 0 }}>
          {selectedAllocations.length > 0 ? (
            <Stack sx={(theme) => ({ [theme.breakpoints.down('md')]: { minWidth: 600 } })}>
              {selectedAllocations.map((allocation) => {
                const groupsWithSameRoom = allocation.room
                  ? getRoomGroupNames({
                      groupsByRoom,
                      roomId: allocation.room.id,
                      groupId: allocation.group.id,
                    })
                  : undefined;

                return (
                  <TimetableGroupCard
                    key={allocation.group.id}
                    group={allocation.group}
                    room={allocation.room ?? undefined}
                    isSelected
                    onClick={() => handleRemoveGroup(allocation.group)}
                    onSelectRoom={(room) => handleSelectRoom(allocation.group.id, room)}
                    isLoadingFacilities={isLoadingFacilities}
                    facilities={facilities}
                    roomWarningMessage={
                      groupsWithSameRoom
                        ? formatMessage(
                            { id: 'timetable-RoomAlreadyInUse' },
                            { groups: groupsWithSameRoom },
                          )
                        : undefined
                    }
                    ageGroupWarningMessage={
                      hasAgeGroupOutsideScope(allocation.group.age_groups, structureAgeGroupIds)
                        ? formatMessage({ id: 'timetable-GroupAgeGroupNotMatching' })
                        : undefined
                    }
                  />
                );
              })}
            </Stack>
          ) : (
            <ModalEmptyMainArea label={formatMessage({ id: 'assessments-Group' }).toLowerCase()} />
          )}
        </ModalContent>
      </ModalPeopleExtensionPanel>

      <ModalFooter active>
        <Button startIcon={<CheckIcon />} onClick={handleSave}>
          {formatMessage({ id: 'action-Save' })}
        </Button>
      </ModalFooter>
    </ModalSmall>
  );
};

type TimetableGroupCardProps = {
  group: TimetableGroup;
  room?: TimetableRoom;
  onClick: () => void;
  onSelectRoom?: (room: TimetableRoom | null) => void;
  isSelected?: boolean;
  disabled?: boolean;
  isLoadingFacilities?: boolean;
  facilities?: Facility[];
  roomWarningMessage?: string;
  ageGroupWarningMessage?: string;
};

export const TimetableGroupCard: FC<TimetableGroupCardProps> = ({
  group,
  room,
  onClick,
  onSelectRoom,
  isSelected = false,
  disabled = false,
  isLoadingFacilities = false,
  facilities = [],
  roomWarningMessage,
  ageGroupWarningMessage,
}) => {
  const showRoomSelect =
    isSelected && !!onSelectRoom && (isLoadingFacilities || facilities.length > 0);
  const showRoomSelectSkeleton = isLoadingFacilities && !room;

  const { formatMessage } = useIntl();
  const conflict = useGroupConflict().getGroupConflict(group.id);
  return (
    <ListRow
      variant={isSelected ? 'compact' : 'expanded'}
      disabled={disabled}
      onClick={onClick}
      title={
        <Stack direction="row" alignItems="center" gap={0.25} sx={{ minWidth: 0 }}>
          {!!ageGroupWarningMessage && (
            <AttentionTooltipIcon title={ageGroupWarningMessage} severity="error" size="small" />
          )}
          {!!conflict && (
            <AttentionTooltipIcon
              title={formatMessage(
                { id: 'timetable-group-conflict-Warning' },
                { name: conflict.timetableName },
              )}
              severity="warning"
              size="small"
            />
          )}

          <TypographyWithOverflowHint
            className="list-row-title"
            variant="h3"
            color="common.grey2"
            noWrap
            component={Link}
            to={`/groups/${group.id}`}
            target="_blank"
            sx={{ '&:hover': { textDecoration: 'underline' } }}
          >
            {group.name}
          </TypographyWithOverflowHint>
        </Stack>
      }
      titleAdornment={
        <Counter
          disabled
          sx={{
            minWidth: 16,
            maxHeight: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            ml: 0.5,
          }}
        >
          <Typography variant="body3" color="common.main3">
            {group.student_count ?? 0}
          </Typography>
        </Counter>
      }
      subtitle={
        (group.subject || group.type === GroupType.TutorGroup) && (
          <Box
            sx={(theme) => ({
              ...(isSelected && {
                minWidth: 200,
                maxWidth: 200,
                [theme.breakpoints.only('lg')]: { minWidth: 150, maxWidth: 150 },
              }),
            })}
          >
            <TagSubject
              sx={{
                maxWidth: '100%',
                '& .MuiTypography-root': { overflow: 'hidden', textOverflow: 'ellipsis' },
              }}
            >
              {group.type === GroupType.TutorGroup
                ? formatMessage({ id: 'groups-TutorGroup' })
                : group.subject?.name}
            </TagSubject>
          </Box>
        )
      }
      rightContent={
        showRoomSelect && (
          <Box
            onFocus={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            sx={(theme) => ({
              width: 200,
              [theme.breakpoints.only('lg')]: { width: 150 },
            })}
          >
            {showRoomSelectSkeleton ? (
              <Skeleton width={100} />
            ) : (
              <RoomCellSelect
                selectedRoom={room ?? null}
                facilities={facilities}
                onSelect={onSelectRoom}
                isLoading={isLoadingFacilities}
              >
                {!!roomWarningMessage && <AttentionTooltipIcon title={roomWarningMessage} />}
              </RoomCellSelect>
            )}
          </Box>
        )
      }
      actions={
        <CircleIconButton
          className="actionIcon"
          size="small"
          sx={(theme) => ({
            position: 'absolute',
            right: 4,
            color: isSelected ? theme.palette.common.light2 : theme.palette.common.light3,
          })}
        >
          {isSelected ? <MinusIcon /> : <PlusIcon />}
        </CircleIconButton>
      }
      sx={{
        '&:hover': disabled
          ? { backgroundColor: 'transparent' }
          : {
              backgroundColor: theme.palette.common.lightBg,
              '.list-row-title, .list-row-right-content, .tag-subject': {
                color: theme.palette.primary.main,
              },
              '.actionIcon': {
                color: theme.palette.common.light2,
                background: theme.palette.primary.main,
              },
            },
      }}
    />
  );
};
